import { type RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import type { TReportSection } from '@services/types';
import { useReportStore } from '@store/ReportStore';

import { E_SECTION_MODE, subsectionConfig } from '@common/configs';

export const useActiveSection = (sections: TReportSection[]) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const slideRefs = useMemo(() => {
    const map = new Map<string, RefObject<HTMLDivElement | null>>();

    sections.forEach((section) => {
      const mode = subsectionConfig[section.id] ?? E_SECTION_MODE.SINGLE;

      if (mode === E_SECTION_MODE.MULTI && 'data' in section && section.data) {
        Object.keys(section.data).forEach((key) => {
          const refId = `${section.id}::${key}`;
          map.set(refId, { current: null });
        });
      } else {
        const refId = section.id;
        map.set(refId, { current: null });
      }
    });
    return map;
  }, [sections]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let mostVisible: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting && (!mostVisible || entry.intersectionRatio > mostVisible.intersectionRatio)) {
            mostVisible = entry;
          }
        }

        if (mostVisible?.target) {
          const subsectionId = mostVisible.target.getAttribute('data-id');
          if (subsectionId) {
            const [sectionId] = subsectionId.split('::');
            if (sectionId !== activeId) {
              setActiveId(sectionId);
            }
          }
        }
      },
      { threshold: [0.5, 0.6, 0.75] }
    );

    slideRefs.forEach((ref, id) => {
      if (ref.current) {
        ref.current.setAttribute('data-id', id);
        observer.observe(ref.current);
      }
    });

    return () => {
      return observer.disconnect();
    };
  }, [slideRefs, activeId]);

  return { activeId, slideRefs };
};

export const useTextEllipsis = (text: string) => {
  const MAX_LIMIT = 255;
  const shouldEllipsis = text.length > MAX_LIMIT;

  const displayText = useMemo(() => {
    if (!shouldEllipsis) {
      return text;
    }
    return `${text.slice(0, MAX_LIMIT)}`;
  }, [text, shouldEllipsis]);

  return {
    shouldEllipsis,
    displayText,
  };
};

export const useReportPageData = (id?: string) => {
  const report = useReportStore((store) => {
    return store.report;
  });
  const restoreReport = useReportStore((s) => {
    return s.restoreReport;
  });
  const sections = report?.sections ?? [];

  useEffect(() => {
    if (id && !report) {
      restoreReport(id);
    }
  }, [id, report, restoreReport]);

  return { report, sections };
};

export const useReportActions = () => {
  const [showReportActions, setShowReportActions] = useState(false);

  const closeReportActions = useCallback(() => {
    setShowReportActions(false);
  }, []);

  const openReportActions = useCallback(() => {
    setShowReportActions(true);
  }, []);

  return { showReportActions, openReportActions, closeReportActions };
};
